import { useState } from 'react';
import {
  Card,
  EmptyState,
  ErrorBanner,
  PrimaryButton,
  Screen,
  TopBar,
} from '../components/Chrome';
import { CoursePicker } from '../components/CoursePicker';
import { HelpCodeDialog } from '../components/HelpCodeDialog';
import { WINDOW_SECONDS, useCheckIn } from '../hooks/useCheckIn';
import type { CheckInState } from '../hooks/useCheckIn';
import { CourseRegistrationScreen } from './CourseRegistrationScreen';

/**
 * One screen, one job: get this student marked present.
 *
 * The layout follows the attempt rather than the menu — pick a course, press one
 * button, watch a single progress surface, and land on exactly one of three
 * outcomes. The lecturer's code is deliberately not on the first screen: it only
 * appears once the automatic attempt has actually failed.
 *
 * Mirrors ui/student/LectureEntryScreen.kt.
 */
export function CheckInScreen({ email, onSignOut }: { email: string; onSignOut: () => void }) {
  const checkIn = useCheckIn();
  const { state } = checkIn;
  const [view, setView] = useState<'checkin' | 'register'>('checkin');

  if (view === 'register') {
    return (
      <CourseRegistrationScreen
        onBack={() => {
          setView('checkin');
          void checkIn.refreshRegistered();
        }}
      />
    );
  }

  return (
    <>
      <Screen top={<TopBar email={email} onSignOut={onSignOut} />}>
        <Card>
          {state.outcome === 'present' ? (
            <Outcome
              variant="present"
              emoji="✅"
              title="You're marked present"
              body="Your attendance for this lecture is recorded."
              onDone={checkIn.markAnotherCourse}
            />
          ) : state.outcome === 'flagged' ? (
            <Outcome
              variant="flagged"
              emoji="🕓"
              title="Under review"
              body="We couldn't verify that you were present in the lecture room. Your attendance is now pending review by the lecturer."
              onDone={checkIn.markAnotherCourse}
            />
          ) : (
            <>
              {state.courses.length === 0 ? (
                <NoLecturesRunning error={state.error} />
              ) : (
                <CheckInBody checkIn={checkIn} />
              )}
              <button
                type="button"
                className="button--link"
                style={{
                  display: 'block',
                  margin: '4px auto 0',
                  padding: '2px 4px',
                  color: 'var(--bt-mid)',
                  textDecoration: 'underline',
                }}
                onClick={() => setView('register')}
              >
                Register for courses
              </button>
            </>
          )}
        </Card>
      </Screen>

      {state.helpDialogOpen && (
        <HelpCodeDialog
          submitting={state.helpSubmitting}
          error={state.helpError}
          onDismiss={checkIn.dismissHelp}
          onSubmit={checkIn.submitHelpCode}
        />
      )}
    </>
  );
}

function CheckInBody({ checkIn }: { checkIn: ReturnType<typeof useCheckIn> }) {
  const { state, running, busy } = checkIn;

  return (
    <>
      <h1 className="title">Mark your attendance</h1>
      <p className="subtitle">
        Choose your lecture and hold still for a moment while we confirm you're in the lecture
        room.
      </p>

      {state.error && (
        <div style={{ marginTop: 14 }}>
          <ErrorBanner message={state.error} />
        </div>
      )}

      <div style={{ marginTop: 18 }}>
        <CoursePicker
          courses={state.courses}
          selectedId={state.selectedCourseId}
          disabled={busy}
          registeredIds={state.registeredIds}
          onSelect={checkIn.selectCourse}
        />
      </div>

      <div style={{ marginTop: 10 }}>
        {running ? (
          <RunningPanel state={state} onCancel={checkIn.cancelCheckIn} />
        ) : state.needsHelp ? (
          <NeedsHelpPanel
            onTryAgain={() => {
              checkIn.tryAgain();
              checkIn.startCheckIn();
            }}
            onGetHelp={checkIn.openHelp}
          />
        ) : (
          <PrimaryButton
            text="Check me in"
            variant="bluetooth"
            disabled={!state.selectedCourseId || busy}
            onClick={checkIn.startCheckIn}
          />
        )}
      </div>
    </>
  );
}

/** The single progress surface for the whole 90-second window. */
function RunningPanel({ state, onCancel }: { state: CheckInState; onCancel: () => void }) {
  const elapsed = WINDOW_SECONDS - state.secondsLeft;
  const progress = Math.min(Math.max(elapsed, 0), WINDOW_SECONDS) / WINDOW_SECONDS;

  return (
    <div className="running">
      {/* Signals "we're listening" without implying which radio is doing the work. */}
      <div className="dot" aria-hidden="true">
        <div className="dot__inner" />
      </div>
      <div className="running__title">
        {state.phase === 'preparing' ? 'Getting ready…' : "Confirming you're in the lecture room"}
      </div>
      <div className="running__hint">
        Stay where you are. This takes up to {WINDOW_SECONDS} seconds.
      </div>
      <div
        className="progress"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={WINDOW_SECONDS}
        aria-valuenow={elapsed}
      >
        <div className="progress__fill" style={{ width: `${progress * 100}%` }} />
      </div>
      <div className="running__remaining">{state.secondsLeft}s remaining</div>
      <button type="button" className="button--link" onClick={onCancel}>
        Cancel
      </button>
    </div>
  );
}

/**
 * Shown only after a full window failed. Two ways forward and no explanation of
 * *why* it failed — the app genuinely does not know, and guessing out loud would
 * tell a cheat how far off they are.
 */
function NeedsHelpPanel({
  onTryAgain,
  onGetHelp,
}: {
  onTryAgain: () => void;
  onGetHelp: () => void;
}) {
  return (
    <div className="needshelp">
      <div className="needshelp__emoji" aria-hidden="true">
        🤔
      </div>
      <div className="needshelp__title">We couldn't confirm you're in the lecture</div>
      <div className="needshelp__body">Try once more, or ask your lecturer for the attendance code.</div>
      <div className="stack">
        <PrimaryButton text="Try again" variant="bluetooth" onClick={onTryAgain} />
        <PrimaryButton text="Get help" variant="plain" onClick={onGetHelp} />
      </div>
    </div>
  );
}

function Outcome({
  variant,
  emoji,
  title,
  body,
  onDone,
}: {
  variant: 'present' | 'flagged';
  emoji: string;
  title: string;
  body: string;
  onDone: () => void;
}) {
  return (
    <div className="outcome">
      <div className={`outcome__badge outcome__badge--${variant}`} aria-hidden="true">
        {emoji}
      </div>
      <h1 className="outcome__title">{title}</h1>
      <p className={`outcome__body outcome__body--${variant}`}>{body}</p>
      <PrimaryButton text="Mark another lecture" onClick={onDone} />
    </div>
  );
}

function NoLecturesRunning({ error }: { error: string | null }) {
  return (
    <div>
      {error && (
        <div style={{ marginBottom: 12 }}>
          <ErrorBanner message={error} />
        </div>
      )}
      <EmptyState
        icon="📅"
        title="No lectures running right now"
        text="When one of your sessions starts it appears here automatically. This list refreshes every 10 seconds."
      />
    </div>
  );
}
